/**
 * useProjectList — owns the Explorer's project-list state.
 *
 * Initial fetch on mount + WebSocket-driven refetch when a project event
 * (`create_project`, `update_project`, `delete_project`) arrives.
 *
 * We deliberately refetch on every relevant completed event rather than
 * patching the list in place. The list is small (limit 50), the API call is
 * cheap, and refetching keeps the displayed activity_count / project_name
 * in sync with the server without re-implementing every mutation's payload
 * shape on the client. Caches are an optimization, never a source of truth.
 *
 * Owns: project list state, initial fetch, refetch on WS events.
 * Does NOT own: WebSocket connection or selection.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { projectAPI } from '@/lib/api/projects'
import type { ProjectDetail } from '@/lib/types/project-graph'
import type { ToolCallEvent } from './useWorkspaceSocket'

const PROJECT_LIST_TOOLS = new Set(['create_project', 'update_project', 'delete_project'])

interface UseProjectListReturn {
  projects: ProjectDetail[]
  loading: boolean
  refetch: () => Promise<void>
}

interface UseProjectListOptions {
  /** WebSocket events from useWorkspaceSocket. Pass [] when no socket. */
  events?: ToolCallEvent[]
}

export function useProjectList({ events = [] }: UseProjectListOptions = {}): UseProjectListReturn {
  const [projects, setProjects] = useState<ProjectDetail[]>([])
  const [loading, setLoading] = useState(false)
  const processedCallIdsRef = useRef<Set<string>>(new Set())
  const seededRef = useRef(false)

  const refetch = useCallback(async () => {
    setLoading(true)
    try {
      const response = await projectAPI.listProjects({ limit: 50 })
      setProjects(response.data.projects || [])
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    refetch().catch((err) => {
      // System boundary: surface the failure to the console but don't crash
      // the panel — the user can manually retry via the Refresh button.
      console.error('useProjectList: initial fetch failed', err)
    })
  }, [refetch])

  // React to WS project events
  useEffect(() => {
    if (events.length === 0) return

    // On first run (or first events delivery), seed processedCallIds with
    // *completed* events that pre-date mount, so we don't refetch for history.
    // We deliberately do NOT seed `started` events: they may still have a
    // matching `completed` arriving after mount (in-flight tool calls, or
    // recent started events kept by sessionStorage's 5-minute window). If we
    // seeded those, the post-mount `completed` would share a call_id with the
    // dedupe set and be skipped — which is the ecosemantic-e0d regression.
    // Mirrors useGraphRefresh's seeding (it gates on status === 'completed').
    if (!seededRef.current) {
      seededRef.current = true
      for (const ev of events) {
        if (ev.call_id && ev.data.status === 'completed') {
          processedCallIdsRef.current.add(ev.call_id)
        }
      }
      return
    }

    let shouldRefetch = false
    for (const ev of events) {
      if (ev.data.status !== 'completed' || !ev.call_id) continue
      if (processedCallIdsRef.current.has(ev.call_id)) continue
      processedCallIdsRef.current.add(ev.call_id)

      if (PROJECT_LIST_TOOLS.has(ev.data.tool_name)) {
        shouldRefetch = true
      }
    }

    if (shouldRefetch) {
      refetch().catch((err) => {
        console.error('useProjectList: WS-triggered refetch failed', err)
      })
    }

    // Cap the Set size (mirrors useGraphRefresh convention)
    if (processedCallIdsRef.current.size > 200) {
      const arr = [...processedCallIdsRef.current]
      processedCallIdsRef.current = new Set(arr.slice(-100))
    }
  }, [events, refetch])

  return { projects, loading, refetch }
}
